/**
 * TASK VALIDATION AND DATA PROCESSING HELPERS
 *
 * This module contains pure functions for validating and processing task data
 * during mutation operations. These functions handle the complex logic of
 * input validation, data filtering, and assignee tracking.
 *
 * Key Features:
 * - Input validation for mutation operations
 * - Database field filtering and sanitization
 * - Assignee change tracking for cache invalidation
 * - Offline data validation and processing
 * - Type-safe update object creation
 */

import { TaskData } from '../taskTypes';
import { CLIENT_ID } from '../../../lib/client-context';

/**
 * Valid database fields for task updates
 * These are the only fields that should be sent to the database
 */
export const VALID_TASK_DB_FIELDS = [
  'status',
  'deadline',
  'tags',
  'is_highlighted',
  'assigned_to',
  'position',
  'title',
  'description',
] as const;

/**
 * Type for valid database field keys
 */
export type ValidTaskDbField = (typeof VALID_TASK_DB_FIELDS)[number];

/**
 * Input for batch update validation
 */
export interface BatchUpdateValidationInput {
  taskIds: string[];
  updates: Record<string, unknown>;
  topicId: string;
}

/**
 * Result of batch update validation
 */
export interface BatchUpdateValidationResult {
  isValid: boolean;
  filteredUpdates: Record<string, unknown>;
  errors: string[];
  affectedFields: string[];
}

/**
 * Input for assignee change tracking
 */
export interface AssigneeTrackingInput {
  currentAssignedTo?: string | null;
  newAssignedTo?: string | null;
  taskId: string;
}

/**
 * Result of assignee change tracking
 */
export interface AssigneeTrackingResult {
  hasChanges: boolean;
  oldAssignee?: string;
  newAssignee?: string;
  affectedAssignees: string[];
}

/**
 * Validates and filters task update data for database operations
 *
 * This function ensures that only valid database fields are included in updates
 * and adds necessary metadata fields like updated_at and client_id.
 *
 * @param updates - Raw update object from user input
 * @returns Filtered and validated update object ready for database
 */
export function validateAndFilterTaskUpdates(
  updates: Record<string, unknown>
): BatchUpdateValidationResult {
  const errors: string[] = [];
  const affectedFields: string[] = [];

  // Validate input
  if (!updates || typeof updates !== 'object') {
    return {
      isValid: false,
      filteredUpdates: {},
      errors: ['Updates object is required and must be an object'],
      affectedFields: [],
    };
  }

  // Start with base update fields
  const filteredUpdates: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };

  // Process each update field
  Object.entries(updates).forEach(([key, value]) => {
    // Skip undefined values
    if (value === undefined) {
      return;
    }

    // Check if field is valid for database updates
    if (VALID_TASK_DB_FIELDS.includes(key as ValidTaskDbField)) {
      // Validate specific field types
      const validationResult = validateTaskField(key as ValidTaskDbField, value);

      if (validationResult.isValid) {
        filteredUpdates[key] = validationResult.sanitizedValue ?? value;
        affectedFields.push(key);
      } else {
        errors.push(`Invalid value for field '${key}': ${validationResult.error}`);
      }
    } else {
      // Skip invalid fields but don't error - just log for debugging
      console.warn(`Skipping invalid database field: ${key}`);
    }
  });

  // Add metadata
  filteredUpdates.metadata = {
    client_id: CLIENT_ID,
    updated_at: new Date().toISOString(),
  };

  return {
    isValid: errors.length === 0,
    filteredUpdates,
    errors,
    affectedFields,
  };
}

/**
 * Validates individual task field values
 */
interface FieldValidationResult {
  isValid: boolean;
  error?: string;
  sanitizedValue?: unknown;
}

function validateTaskField(field: ValidTaskDbField, value: unknown): FieldValidationResult {
  switch (field) {
    case 'status':
      if (typeof value !== 'string') {
        return { isValid: false, error: 'Status must be a string' };
      }
      // Could add enum validation here if status values are defined
      return { isValid: true };

    case 'deadline':
      if (value !== null && typeof value !== 'string') {
        return { isValid: false, error: 'Deadline must be a string or null' };
      }
      // Could add date format validation here
      return { isValid: true };

    case 'tags': {
      if (!Array.isArray(value)) {
        return { isValid: false, error: 'Tags must be an array' };
      }
      // Ensure all tags are strings
      const allStrings = value.every(tag => typeof tag === 'string');
      if (!allStrings) {
        return { isValid: false, error: 'All tags must be strings' };
      }
      return { isValid: true };
    }

    case 'is_highlighted':
      if (typeof value !== 'boolean') {
        return { isValid: false, error: 'is_highlighted must be a boolean' };
      }
      return { isValid: true };

    case 'assigned_to':
      if (value !== null && typeof value !== 'string') {
        return { isValid: false, error: 'assigned_to must be a string or null' };
      }
      return { isValid: true };

    case 'position':
      if (typeof value !== 'number' || value < 0) {
        return { isValid: false, error: 'Position must be a non-negative number' };
      }
      return { isValid: true };

    case 'title':
      if (typeof value !== 'string' || value.trim().length === 0) {
        return { isValid: false, error: 'Title must be a non-empty string' };
      }
      return { isValid: true, sanitizedValue: value.trim() };

    case 'description':
      if (value !== null && typeof value !== 'string') {
        return { isValid: false, error: 'Description must be a string or null' };
      }
      return { isValid: true, sanitizedValue: typeof value === 'string' ? value.trim() : value };

    default:
      return { isValid: false, error: `Unknown field: ${field}` };
  }
}

/**
 * Validates batch update input parameters
 *
 * @param input - Batch update parameters to validate
 * @returns Validation result with detailed error information
 */
export function validateBatchUpdateInput(
  input: BatchUpdateValidationInput
): BatchUpdateValidationResult {
  const { taskIds, updates, topicId } = input;
  const errors: string[] = [];

  // Validate taskIds
  if (!Array.isArray(taskIds) || taskIds.length === 0) {
    errors.push('At least one task ID is required');
  } else {
    // Validate each task ID
    taskIds.forEach((id, index) => {
      if (typeof id !== 'string' || id.trim().length === 0) {
        errors.push(`Task ID at index ${index} must be a non-empty string`);
      }
    });
  }

  // Validate topicId
  if (typeof topicId !== 'string' || topicId.trim().length === 0) {
    errors.push('Topic ID must be a non-empty string');
  }

  // Validate updates object
  if (!updates || typeof updates !== 'object') {
    errors.push('Updates object is required');
  }

  // If basic validation passes, validate and filter the updates
  if (errors.length === 0) {
    const updateValidation = validateAndFilterTaskUpdates(updates);
    return {
      ...updateValidation,
      errors: [...errors, ...updateValidation.errors],
    };
  }

  return {
    isValid: false,
    filteredUpdates: {},
    errors,
    affectedFields: [],
  };
}

/**
 * Tracks assignee changes for cache invalidation purposes
 *
 * This function identifies which assignees are affected by a task update
 * so that the appropriate cache keys can be invalidated.
 *
 * @param input - Assignee tracking parameters
 * @returns Information about assignee changes
 */
export function trackAssigneeChanges(input: AssigneeTrackingInput): AssigneeTrackingResult {
  const { currentAssignedTo, newAssignedTo } = input;

  // Normalize null/undefined to null for comparison
  const normalizedCurrent = currentAssignedTo || null;
  const normalizedNew = newAssignedTo || null;

  // Check if assignment is actually changing
  const hasChanges = normalizedCurrent !== normalizedNew;

  if (!hasChanges) {
    return {
      hasChanges: false,
      affectedAssignees: [],
    };
  }

  const affectedAssignees: string[] = [];

  // Add old assignee if they're losing the task
  if (normalizedCurrent) {
    affectedAssignees.push(normalizedCurrent);
  }

  // Add new assignee if they're gaining the task
  if (normalizedNew) {
    affectedAssignees.push(normalizedNew);
  }

  return {
    hasChanges: true,
    oldAssignee: normalizedCurrent || undefined,
    newAssignee: normalizedNew || undefined,
    affectedAssignees,
  };
}

/**
 * Tracks assignee changes for multiple tasks in a batch update
 *
 * @param tasks - Array of current tasks
 * @param newAssignedTo - New assignee value from updates
 * @returns Set of all affected assignee IDs
 */
export function trackBatchAssigneeChanges(
  tasks: TaskData[],
  newAssignedTo: string | null | undefined
): Set<string> {
  const affectedAssignees = new Set<string>();

  // Only process if assignedTo is being changed
  if (newAssignedTo === undefined) {
    return affectedAssignees;
  }

  tasks.forEach(task => {
    const result = trackAssigneeChanges({
      currentAssignedTo: task.assigned_to,
      newAssignedTo,
      taskId: task.id,
    });

    result.affectedAssignees.forEach(assigneeId => {
      affectedAssignees.add(assigneeId);
    });
  });

  return affectedAssignees;
}

/**
 * Validates that a task exists and is not deleted
 *
 * @param task - Task data to validate
 * @param taskId - Expected task ID
 * @returns Validation result
 */
export function validateTaskExists(
  task: TaskData | null,
  taskId: string
): {
  isValid: boolean;
  error?: string;
} {
  if (!task) {
    return {
      isValid: false,
      error: `Task ${taskId} not found`,
    };
  }

  if (task.isDeleted) {
    return {
      isValid: false,
      error: `Task ${taskId} has been deleted`,
    };
  }

  if (task.id !== taskId) {
    return {
      isValid: false,
      error: `Task ID mismatch: expected ${taskId}, got ${task.id}`,
    };
  }

  return { isValid: true };
}

/**
 * Validates reorder operation parameters
 */
export interface ReorderValidationInput {
  taskId: string;
  topicId: string;
  newIndex: number;
  newStatus?: string;
}

export function validateReorderInput(input: ReorderValidationInput): {
  isValid: boolean;
  errors: string[];
} {
  const { taskId, topicId, newIndex, newStatus } = input;
  const errors: string[] = [];

  // Validate taskId
  if (typeof taskId !== 'string' || taskId.trim().length === 0) {
    errors.push('Task ID must be a non-empty string');
  }

  // Validate topicId
  if (typeof topicId !== 'string' || topicId.trim().length === 0) {
    errors.push('Topic ID must be a non-empty string');
  }

  // Validate newIndex
  if (typeof newIndex !== 'number' || newIndex < 0 || !Number.isInteger(newIndex)) {
    errors.push('New index must be a non-negative integer');
  }

  // Validate newStatus if provided
  if (newStatus !== undefined) {
    if (typeof newStatus !== 'string' || newStatus.trim().length === 0) {
      errors.push('New status must be a non-empty string if provided');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Creates a standardized update object with metadata
 *
 * @param updates - Base updates to apply
 * @returns Update object with metadata
 */
export function createTaskUpdateObject(updates: Record<string, unknown>): Record<string, unknown> {
  return {
    ...updates,
    updated_at: new Date().toISOString(),
    metadata: {
      client_id: CLIENT_ID,
      updated_at: new Date().toISOString(),
    },
  };
}
